import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Lock, User, LogIn, AlertCircle } from "lucide-react";
import { BrandMark } from "../layouts/BrandMark";
import { pageBaseForRole, useAuth } from "../context/AuthContext";
import { ApiError } from "../api/types";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginVal, setLoginVal] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Если уже залогинены — отправляем на стартовую страницу его роли.
  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from || pageBaseForRole(user.role)} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(loginVal.trim(), password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from || pageBaseForRole(u.role), { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 0) setError("Сервер недоступен. Проверьте сеть и адрес API.");
        else if (e.status === 401) setError("Неверный логин или пароль.");
        else setError(e.message || "Не удалось войти");
      } else {
        setError("Не удалось войти");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-surface-2)] px-4 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-6 flex justify-center">
          <BrandMark to="/login" />
        </div>

        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-[0_12px_40px_-16px_rgba(15,23,42,0.18)]">
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-[var(--color-fg)]">
            Вход в АстраМаркет
          </h1>
          <p className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Используйте корпоративный логин и пароль.
          </p>

          <form onSubmit={onSubmit} className="mt-5 space-y-3">
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                Логин
              </label>
              <div className="relative">
                <User
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]"
                />
                <input
                  value={loginVal}
                  onChange={(e) => setLoginVal(e.target.value)}
                  autoComplete="username"
                  required
                  autoFocus
                  placeholder="petrov.a"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-9 pr-3 font-mono text-[13px] text-[var(--color-fg)] outline-none transition-colors placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)]"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                Пароль
              </label>
              <div className="relative">
                <Lock
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]"
                />
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-9 pr-3 text-[13px] text-[var(--color-fg)] outline-none transition-colors placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)]"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger-bg)] px-3 py-2 text-[12px] text-[var(--color-danger)]">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !loginVal || !password}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-brand)] px-3 py-2.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Spinner />
                  <span>Вход…</span>
                </>
              ) : (
                <>
                  <LogIn size={15} />
                  <span>Войти</span>
                </>
              )}
            </button>
          </form>

          <p className="mt-5 text-center text-[11px] text-[var(--color-fg-faint)]">
            Демо: <span className="font-mono text-[var(--color-fg-muted)]">petrov.a / demo1234</span>
          </p>
        </section>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
  );
}
